import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';

export type TooltipPosition = 'above' | 'below' | 'left' | 'right';

/**
 * The portal's tooltip.
 *
 * It wraps its single child, clones it, and attaches the listeners - so the
 * tooltip is configured where the element is used rather than by a separate
 * registration:
 *
 *   <Tooltip message={collapsed ? item.title : ''} position="right" showDelay={350}>
 *     <button>...</button>
 *   </Tooltip>
 *
 * An empty or missing `message` renders the child untouched and wires up
 * nothing. The sidebar relies on that to suppress tooltips when the rail is
 * expanded and the labels are already visible.
 *
 * The panel is portalled to `<body>` so an `overflow: hidden` ancestor (every
 * scrolling nav and grid pane in the portal) cannot clip it, and it carries
 * the `.cw-tooltip` hooks the feature stylesheets restyle it through.
 */
export interface TooltipProps {
  message?: string | null;
  position?: TooltipPosition;
  showDelay?: number;
  hideDelay?: number;
  disabled?: boolean;
  children: ReactNode;
}

interface Coords {
  top: number;
  left: number;
}

const GAP = 8;

export function Tooltip({
  message,
  position = 'below',
  showDelay = 0,
  hideDelay = 0,
  disabled = false,
  children
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  const active = !disabled && !!message && String(message).trim().length > 0;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    if (!active) {
      return;
    }

    clearTimer();
    timerRef.current = setTimeout(() => setVisible(true), showDelay);
  }, [active, clearTimer, showDelay]);

  const hide = useCallback(() => {
    clearTimer();

    if (hideDelay > 0) {
      timerRef.current = setTimeout(() => setVisible(false), hideDelay);
      return;
    }

    setVisible(false);
  }, [clearTimer, hideDelay]);

  useEffect(() => clearTimer, [clearTimer]);

  // Measured after paint, once the panel has a real size: positioning off an
  // assumed width put right-side tooltips through the edge of the viewport.
  useEffect(() => {
    if (!visible) {
      return;
    }

    const anchor = anchorRef.current;
    const panel = panelRef.current;

    if (!anchor || !panel) {
      return;
    }

    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case 'above':
        top = a.top - p.height - GAP;
        left = a.left + a.width / 2 - p.width / 2;
        break;
      case 'left':
        top = a.top + a.height / 2 - p.height / 2;
        left = a.left - p.width - GAP;
        break;
      case 'right':
        top = a.top + a.height / 2 - p.height / 2;
        left = a.right + GAP;
        break;
      default:
        top = a.bottom + GAP;
        left = a.left + a.width / 2 - p.width / 2;
    }

    // Keep the panel inside the viewport rather than letting it introduce a
    // scrollbar.
    left = Math.max(GAP, Math.min(left, window.innerWidth - p.width - GAP));
    top = Math.max(GAP, Math.min(top, window.innerHeight - p.height - GAP));

    setCoords({ top, left });
  }, [visible, position]);

  // Any scroll detaches the panel from its anchor, so dismiss instead of
  // chasing the element around.
  useEffect(() => {
    if (!visible) {
      return;
    }

    const dismiss = () => setVisible(false);

    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);

    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [visible]);

  if (!isValidElement(children)) {
    return <>{children}</>;
  }

  const child = children as ReactElement<Record<string, unknown>>;

  if (!active) {
    return child;
  }

  const childProps = child.props;

  const call = (name: string, event: unknown) => {
    const handler = childProps[name];

    if (typeof handler === 'function') {
      (handler as (e: unknown) => void)(event);
    }
  };

  const cloned = cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;

      // Preserve whatever ref the caller already put on the child.
      const existing = (child as unknown as { ref?: unknown }).ref;

      if (typeof existing === 'function') {
        (existing as (n: HTMLElement | null) => void)(node);
      } else if (existing && typeof existing === 'object') {
        (existing as { current: HTMLElement | null }).current = node;
      }
    },
    'aria-describedby': visible ? id : undefined,
    onMouseEnter: (e: unknown) => {
      call('onMouseEnter', e);
      show();
    },
    onMouseLeave: (e: unknown) => {
      call('onMouseLeave', e);
      hide();
    },
    onFocus: (e: unknown) => {
      call('onFocus', e);
      show();
    },
    onBlur: (e: unknown) => {
      call('onBlur', e);
      hide();
    }
  } as Record<string, unknown>);

  return (
    <>
      {cloned}
      {visible &&
        createPortal(
          <div
            id={id}
            ref={panelRef}
            role="tooltip"
            className={`cw-tooltip cw-tooltip cw-tooltip--${position}`}
            style={{ top: coords.top, left: coords.left }}
          >
            {message}
          </div>,
          document.body
        )}
    </>
  );
}

export default Tooltip;
